import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../i18n'; // Import new auto-translation function
import { translations } from '../translations'; // Import translations
import ApplicationDetail from './ApplicationDetail';
import mainpageImage from '../images/hero photos/mainpage.png'; // Import the hero background image
import './Hero.css';
import TherapeuticAreas from './TherapeuticAreas'; // Import the TherapeuticAreas component

function useFadeInOnScroll() {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight - 100) {
        el.classList.add('visible');
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return ref;
}

// Custom hook for directional slide-in animations
function useDirectionalSlideIn(direction = 'left') {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('slide-in-visible');
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );
    
    el.classList.add(`slide-in-${direction}`);
    observer.observe(el);
    
    return () => observer.unobserve(el);
  }, [direction]);
  return ref;
}

const Hero = ({ language = 'bg' }) => {
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [offset, setOffset] = useState(0);

  // Refs for fade-in animations
  const heroCtaRef = useFadeInOnScroll();
  const benefitsRef = useFadeInOnScroll();
  const applicationsRef = useFadeInOnScroll();
  const ctaRef = useFadeInOnScroll();

  // Directional slide-in refs for applications
  const appRefs = [
    useDirectionalSlideIn('left'),
    useDirectionalSlideIn('right'),
    useDirectionalSlideIn('left'),
    useDirectionalSlideIn('right')
  ];

  // Fixed hero content - best slide
  const heroContent = {
    image: mainpageImage,
    title: language === 'bg' ? 'Открийте силата на екзозомите' : 'Discover the Power of Exosomes',
    subtitle: language === 'bg' ? 'Иновативни решения за възстановяване и подмладяване' : 'Innovative solutions for recovery and rejuvenation',
    description: '',
  };

  // Parallax effect for background - quiz1 style
  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.pageYOffset);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Define services with translations (replacing the old benefits)
  const services = language === 'bg' ? [
    {
      icon: <img src="/images/first page/research.svg" alt="Регенерация" className="svg-icon" />,
      title: 'Регенерация',
      description: 'Екзозомите <b>стимулират естествените процеси</b> на възстановяване в организма и активират <b>регенеративния потенциал</b> на клетките.',
    },
    {
      icon: <img src="/images/first page/renewal.svg" alt="Обновление" className="svg-icon" />,
      title: 'Обновление',
      description: 'Екзозомите активират <b>клетъчните механизми</b> за самообновяване и подпомагат <b>подмладяването</b> на всички тъкани.',
    },
    {
      icon: <img src="/images/first page/protection.svg" alt="Защита" className="svg-icon" />,
      title: 'Защита',
      description: 'Екзозомите укрепват <b>защитните функции</b> на организма и подобряват <b>имунния отговор</b> срещу вредни фактори.',
    },
    {
      icon: <img src="/images/first page/revitalization.svg" alt="Ревитализация" className="svg-icon" />,
      title: 'Ревитализация',
      description: 'Екзозомите подобряват <b>клетъчния метаболизъм</b> и повишават нивата на <b>енергия и виталност</b> в организма.',
    }
  ] : [
    {
      icon: <img src="/images/first page/research.svg" alt="Regeneration" className="svg-icon" />,
      title: 'Regeneration',
      description: 'Exosomes <b>stimulate natural processes</b> of recovery in the body and activate the <b>regenerative potential</b> of cells.',
    },
    {
      icon: <img src="/images/first page/renewal.svg" alt="Renewal" className="svg-icon" />,
      title: 'Renewal',
      description: 'Exosomes activate <b>cellular mechanisms</b> for self-renewal and support <b>rejuvenation</b> of all tissues.',
    },
    {
      icon: <img src="/images/first page/protection.svg" alt="Protection" className="svg-icon" />,
      title: 'Protection',
      description: 'Exosomes strengthen the <b>defensive functions</b> of the body and improve the <b>immune response</b> against harmful factors.',
    },
    {
      icon: <img src="/images/first page/revitalization.svg" alt="Revitalization" className="svg-icon" />,
      title: 'Revitalization',
      description: 'Exosomes improve <b>cellular metabolism</b> and increase levels of <b>energy and vitality</b> in the body.',
    }
  ];

  // Define applications with translations
  const bgApplications = [
    {
      number: "01",
      icon: "✨",
      title: 'Козметична терапия',
      subtitle: 'Подмладяване на кожата',
      description: 'Естествено подмладяване и възстановяване на кожата',
      image: "/images/first page/beauty-face.jpeg",
      details: {
        fullTitle: 'Козметична терапия с екзозоми',
        scientificBasis: 'Екзозомите съдържат растежни фактори, цитокини и микро-РНК, които стимулират регенерацията на кожните клетки.',
        benefits: [
          'Увеличава производството на колаген тип I и III',
          'Подобрява еластичността на кожата с до 30%',
          'Намалява фините линии и бръчки',
          'Подобрява хидратацията и сияйността на кожата'
        ],
        procedure: 'Екзозомите се прилагат чрез микроинжекции или мезотерапия в дермалния слой на кожата.',
        results: 'Първите резултати се виждат след 7-14 дни, с оптимални резултати след 4-6 седмици.'
      }
    },
    {
      number: "02",
      icon: "💇‍♀️", 
      title: 'Терапия за коса',
      subtitle: 'Стимулиране на растежа',
      description: 'Ефективно лечение за косопад и стимулиране на растежа',
      image: "/images/first page/exosomes-hair-therapy.jpeg",
      details: {
        fullTitle: 'Терапия за коса с екзозоми',
        scientificBasis: 'Екзозомите активират стволовите клетки на космените фоликули и стимулират анагенната фаза на растеж.',
        benefits: [
          'Увеличава дебелината на космените нишки с до 23%',
          'Стимулира растежа на нова коса в неактивни фоликули',
          'Подобрява кръвообращението в скалпа',
          'Намалява воспалението, което причинява косопад'
        ],
        procedure: 'Екзозомите се инжектират директно в скалпа на дълбочина 2-3 мм с микроигли.',
        results: 'Спирането на косопада се наблюдава след 4-6 седмици, новият растеж започва след 8-12 седмици.'
      }
    },
    {
      number: "03",
      icon: "🌟",
      title: 'Грижа за кожата',
      subtitle: 'Здрава и сияйна кожа',
      description: 'Комплексни решения за всички типове кожа',
      image: "/images/first page/Exosomes_Skincare-1250x759-1.jpg",
      details: {
        fullTitle: 'Комплексна грижа за кожата',
        scientificBasis: 'Екзозомите модулират клетъчната комуникация и активират защитните механизми на кожата.',
        benefits: [
          'Нормализира себумната продукция',
          'Намалява акне и поствоспалителна хиперпигментация',
          'Подобрява бариерната функция на кожата',
          'Стимулира естествената детоксикация на клетките'
        ],
        procedure: 'Комбинирано приложение чрез серуми, маски и микроинжекции според типа кожа.',
        results: 'Подобрение в текстурата се вижда след 1 седмица, пълен ефект след 6-8 седмици.'
      }
    },
    {
      number: "04",
      icon: "🛡️",
      title: 'Регенеративна медицина',
      subtitle: 'Възстановяване на тъканите',
      description: 'Иновативни методи за лечение и възстановяване',
      image: "/images/first page/Truth-about-exosomes.png",
      details: {
        fullTitle: 'Регенеративна медицина с екзозоми',
        scientificBasis: 'Екзозомите пренасят биоактивни молекули, които регулират възпаление и тъканна регенерация.',
        benefits: [
          'Ускорява заздравяването на рани с до 40%',
          'Намалява възпалението и болката',
          'Стимулира ангиогенезата (образуване на нови кръвоносни съдове)',
          'Подобрява функцията на увредени тъкани'
        ],
        procedure: 'Прилага се интрадермално, интрамускулно или интравенозно според индикацията.',
        results: 'Намаляване на възпалението след 48-72 часа, тъканна регенерация в рамките на 2-4 седмици.'
      }
    }
  ];

  const enApplications = [
    {
      number: "01",
      icon: "✨",
      title: 'Cosmetic Therapy',
      subtitle: 'Skin Rejuvenation',
      description: 'Natural rejuvenation and skin restoration',
      image: "/images/first page/beauty-face.jpeg",
      details: {
        fullTitle: 'Exosome Cosmetic Therapy',
        scientificBasis: 'Exosomes contain growth factors, cytokines, and micro-RNA that stimulate skin cell regeneration.',
        benefits: [
          'Increases production of type I and III collagen',
          'Improves skin elasticity by up to 30%',
          'Reduces fine lines and wrinkles',
          'Improves skin hydration and radiance'
        ],
        procedure: 'Exosomes are applied through microinjections or mesotherapy in the dermal layer of the skin.',
        results: 'First results are visible after 7-14 days, with optimal results after 4-6 weeks.'
      }
    },
    {
      number: "02",
      icon: "💇‍♀️", 
      title: 'Hair Therapy',
      subtitle: 'Growth Stimulation',
      description: 'Effective treatment for hair loss and growth stimulation',
      image: "/images/first page/exosomes-hair-therapy.jpeg",
      details: {
        fullTitle: 'Exosome Hair Therapy',
        scientificBasis: 'Exosomes activate hair follicle stem cells and stimulate the anagen phase of growth.',
        benefits: [
          'Increases hair strand thickness by up to 23%',
          'Stimulates new hair growth in inactive follicles',
          'Improves scalp circulation',
          'Reduces inflammation that causes hair loss'
        ],
        procedure: 'Exosomes are injected directly into the scalp at a depth of 2-3 mm with microneedles.',
        results: 'Hair loss reduction is observed after 4-6 weeks, new growth begins after 8-12 weeks.'
      }
    },
    {
      number: "03",
      icon: "🌟",
      title: 'Skin Care',
      subtitle: 'Healthy and Radiant Skin',
      description: 'Comprehensive solutions for all skin types',
      image: "/images/first page/Exosomes_Skincare-1250x759-1.jpg",
      details: {
        fullTitle: 'Comprehensive Skin Care',
        scientificBasis: 'Exosomes modulate cellular communication and activate skin defense mechanisms.',
        benefits: [
          'Normalizes sebum production',
          'Reduces acne and post-inflammatory hyperpigmentation',
          'Improves skin barrier function',
          'Stimulates natural cell detoxification'
        ],
        procedure: 'Combined application through serums, masks, and microinjections according to skin type.',
        results: 'Improvement in texture is visible after 1 week, full effect after 6-8 weeks.'
      }
    },
    {
      number: "04",
      icon: "🛡️",
      title: 'Regenerative Medicine',
      subtitle: 'Tissue Restoration',
      description: 'Innovative methods for treatment and recovery',
      image: "/images/first page/Truth-about-exosomes.png",
      details: {
        fullTitle: 'Regenerative Medicine with Exosomes',
        scientificBasis: 'Exosomes carry bioactive molecules that regulate inflammation and tissue regeneration.',
        benefits: [
          'Accelerates wound healing by up to 40%',
          'Reduces inflammation and pain',
          'Stimulates angiogenesis (formation of new blood vessels)',
          'Improves damaged tissue function'
        ],
        procedure: 'Applied intradermally, intramuscularly, or intravenously according to indication.',
        results: 'Inflammation reduction after 48-72 hours, tissue regeneration within 2-4 weeks.'
      }
    }
  ];

  // Select applications based on language
  const applications = language === 'bg' ? bgApplications : enApplications;

  const scrollToNextSection = () => {
    const benefitsSection = document.querySelector('.benefits-section');
    if (benefitsSection) {
      benefitsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleApplicationLearnMore = (applicationNumber) => {
    setSelectedDetail(applicationNumber);
  };

  const closeApplicationDetail = () => {
    setSelectedDetail(null);
  };

  // Mobile-specific hero text logic
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 480;

  // Mobile/desktop hero title and button text
  let heroTitle = heroContent.title;
  let heroButton = language === 'bg' ? 'Научете Повече' : 'Learn More';
  if (language === 'bg' && isMobile) {
    // Remove 'регенеративната' from the title if present
    heroTitle = heroTitle.replace('регенеративната ', '');
    heroButton = 'Свържете се';
  }

  return (
    <>
      {/* Main hero section */}
      <section className="hero">
        <div 
          className="hero-background"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${heroContent.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${offset * 0.5}px)`,
          }}
        />
        <div className="hero-overlay" />
        <div className="hero-content-absolute" ref={heroCtaRef}>
          <h1>{heroTitle}</h1>
          <p>{heroContent.subtitle}</p>
          <Link to="/mscs" className="cta-button">
            {heroButton}
          </Link>
        </div>
        <button className="home-scroll-btn" onClick={scrollToNextSection}>
          <span className="down-arrow">›</span>
        </button>
      </section>

      {/* Services section - updated to match screenshot layout */}
      <section className="services-section">
        <div className="services-container">
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={index}>
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description" dangerouslySetInnerHTML={{ __html: service.description }}></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Science Section - Replaced the CTA section with bright background */}
      <section className="science-section" ref={ctaRef}>
        <div className="content-container">
          <div className="science-content">
            <h2>{language === 'bg' ? 'Научната основа на екзозомите' : 'The Scientific Basis of Exosomes'}</h2>
            <p>{language === 'bg' ? 'Екзозомите са микровезикули, които се отделят от клетките и играят ключова роля в междуклетъчната комуникация.' : 'Exosomes are microvesicles that are secreted by cells and play a key role in intercellular communication.'}</p>
            <p>{language === 'bg' ? 'Те носят протеини, РНК и други биологично активни молекули, които могат да влияят на функцията на клетките-получатели.' : 'They carry proteins, RNA, and other biologically active molecules that can influence the function of recipient cells.'}</p>
            <p>{language === 'bg' ? 'Научните изследвания показват, че екзозомите имат огромен потенциал в регенеративната медицина и лечението на различни заболявания.' : 'Scientific research shows that exosomes have enormous potential in regenerative medicine and the treatment of various diseases.'}</p>
          </div>
        </div>
      </section>

      {/* Therapeutic Areas section */}
      <TherapeuticAreas language={language} />

      {/* Modern CTA Section - replaced with MSCs style */}
      <div className="contact-cta-block">
        <h3>{language === 'bg' ? 'Открийте бъдещето на регенеративната медицина' : 'Discover the Future of Regenerative Medicine'}</h3>
        <p>{language === 'bg'
          ? 'Свържете се с нас, за да научите как екзозомите могат да трансформират вашето здраве и красота.'
          : 'Contact us to learn how exosomes can transform your health and beauty.'}
        </p>
        <a href="/contact" className="contact-btn">
          {language === 'bg' ? 'Свържете се с нас' : 'Contact Us'}
        </a>
      </div>

      {/* Application Detail Modal */}
      {selectedDetail !== null && (
        <ApplicationDetail 
          application={language === 'bg' ? bgApplications[selectedDetail] : enApplications[selectedDetail]}
          onClose={closeApplicationDetail}
          language={language}
        />
      )}
    </>
  );
};

export default Hero; 